import React, { useEffect, useRef, useState } from 'react'
import {
	Circle,
	LegendCircleContainer,
	NonWrapSpan,
	ToolTipBorder,
	ToolTipContentColumn,
	ToolTipContentContainer,
	ToolTipDateContainer,
	ToolTipHorizontalContainer,
	ToolTipItemContainer,
	ToolTipSubItem
} from './styles'

interface ChartToolTipProps {
	legendItems: LegendItem[]
	isReversedLegend?: boolean
	sortByValueDesc?: boolean
	formattedDate: string
	hidden: boolean
	position: { x: number; y: number }
	setRef: any
}

export interface LegendItem {
	color: string
	value: string
	name: string
}

const SUFFIX_MULTIPLIERS: Record<string, number> = {
	k: 1e3,
	M: 1e6,
	B: 1e9,
	T: 1e12,
	P: 1e15,
	E: 1e18
}

const parseFormattedValue = (value: string): number => {
	const trimmed = value.trim()
	if (!trimmed) return 0

	if (trimmed.endsWith('%')) {
		const percent = parseFloat(trimmed.replace(/[^0-9.-]/g, ''))
		return Number.isNaN(percent) ? 0 : percent
	}

	const numberMatch = trimmed.match(/-?\d*\.?\d+/)
	if (!numberMatch) return 0

	const parsed = parseFloat(numberMatch[0])
	if (Number.isNaN(parsed)) return 0

	const suffix = trimmed.match(/[kMBTPE]$/)?.[0]
	return suffix ? parsed * SUFFIX_MULTIPLIERS[suffix] : parsed
}

const ChartToolTip: React.FC<ChartToolTipProps> = ({ legendItems, isReversedLegend, sortByValueDesc, formattedDate, position, hidden, setRef }) => {
	const toolTipRef = useRef<HTMLDivElement>(null)
	const [legendItemsState, setLegendItemsState] = useState<LegendItem[]>([])

	useEffect(() => {
		const newLegendItems = legendItems.slice()

		if (sortByValueDesc) {
			newLegendItems.sort((a, b) => parseFormattedValue(b.value) - parseFormattedValue(a.value))
		} else if (isReversedLegend) {
			newLegendItems.reverse()
		}

		setLegendItemsState(newLegendItems)
	}, [isReversedLegend, sortByValueDesc, legendItems])

	useEffect(() => {
		if (toolTipRef.current && setRef) setRef(toolTipRef.current)
	}, [toolTipRef, setRef])

	return (
		<ToolTipBorder onClick={(e) => e.preventDefault()} ref={toolTipRef} style={hidden ? { left: position.x, top: position.y, visibility: 'hidden' } : { left: position.x, top: position.y }}>
			<ToolTipDateContainer>
				<NonWrapSpan>{formattedDate}</NonWrapSpan>
			</ToolTipDateContainer>
			<ToolTipContentContainer>
				<ToolTipContentColumn>
					{legendItemsState.map((item, index) => (
						<ToolTipHorizontalContainer key={index}>
							<LegendCircleContainer>
								<Circle style={{ backgroundColor: item.color }} />
							</LegendCircleContainer>
							<ToolTipSubItem>
								<NonWrapSpan>{item.name}:</NonWrapSpan>
							</ToolTipSubItem>
						</ToolTipHorizontalContainer>
					))}
				</ToolTipContentColumn>
				<ToolTipContentColumn>
					{legendItemsState.map((item, index) => (
						<ToolTipItemContainer key={index}>
							<ToolTipSubItem>
								<NonWrapSpan>
									<b>{item.value}</b>
								</NonWrapSpan>
							</ToolTipSubItem>
						</ToolTipItemContainer>
					))}
				</ToolTipContentColumn>
			</ToolTipContentContainer>
		</ToolTipBorder>
	)
}

export default ChartToolTip
